import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../Modal';
import DownloadIcon from '!svg-react-loader!images/igniter/svgs/download.svg';
import CheckMark from '!svg-react-loader!images/igniter/svgs/check.svg';

const STEP_MEDIA_TYPE = 'media_type';
const STEP_SUB_TYPE_TAG = 'sub_type_tag';
const STEP_FORMAT_SELECTION = 'format_selection';
const VALID_STEPS = [STEP_MEDIA_TYPE, STEP_SUB_TYPE_TAG, STEP_FORMAT_SELECTION];
// Every bulk-download config in SiteProductType::BULK_DOWNLOAD_CONFIG is
// expected to end in format_selection — it's the universal terminal step and
// the sink for any progression that runs out of configured steps.
const TERMINAL_STEP = STEP_FORMAT_SELECTION;
// Matches CollectionDownloadModalPresenter::DEFAULT_TAG_KEY on the Ruby side.
const DEFAULT_TAG_KEY = 'default';

function encodeDownloadParams(filetype, resolution, productTagId) {
  const parts = [filetype, resolution || ''];
  if (productTagId) parts.push(productTagId);
  return btoa(parts.join('/'));
}

function buildDownloadUrl(downloadHostname, collectionId, siteProductTypeId, encodedParams) {
  return Routes.igniter_collection_start_download_url(collectionId, siteProductTypeId, encodedParams, { host: downloadHostname });
}

// Five minutes is well past when any plausible download has started streaming;
// after that the iframe has done its job and is safe to remove.
const IFRAME_REAP_AFTER_MS = 5 * 60 * 1000;

// Triggers a file download without navigating the current page. The server
// responds with Content-Disposition: attachment, which the browser handles as
// a download instead of a page render. Trade-off: errors from the download
// server (4xx/5xx, missing Content-Disposition) load silently into the hidden
// iframe — the user gets no in-page feedback. The iframe can't be cleaned up
// synchronously: iframe.onload doesn't fire for attachment responses, and
// removing the iframe on a short timer aborts the in-flight download in
// Chromium when large bulk collections take longer than the timer to stream.
// Instead, before appending a new iframe we reap any prior ones older than
// IFRAME_REAP_AFTER_MS. That bounds accumulation at roughly one iframe per
// 5 minutes of active use — a burst of downloads within the window can still
// leave several iframes in the DOM, but they clear on the next trigger past
// the TTL (or on the next page navigation).
function triggerDownload(url) {
  const now = Date.now();
  document.querySelectorAll('iframe[data-bulk-download]').forEach((el) => {
    const createdAt = Number(el.dataset.bulkDownloadCreatedAt);
    if (createdAt && now - createdAt > IFRAME_REAP_AFTER_MS) el.remove();
  });

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.title = '';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.dataset.bulkDownload = 'true';
  iframe.dataset.bulkDownloadCreatedAt = String(now);
  iframe.src = url;
  document.body.appendChild(iframe);
}

function savePreferences(optionsHash, resolution, filetype) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]');
  if (!csrfToken) return;

  fetch('/download/defaults', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': csrfToken.content,
    },
    body: new URLSearchParams({
      resolutionFormatOptionsHash: optionsHash,
      resolution: resolution || '',
      fileformat: filetype,
    }),
  }).catch(err => console.error('Failed to save download preferences:', err));
}

function getStoredPreference(optionsHash) {
  if (typeof window !== 'undefined' && window.widget_defaults && window.widget_defaults[optionsHash]) {
    return window.widget_defaults[optionsHash];
  }
  return null;
}

function CollectionDownloadModal({
  section_name: sectionName,
  media_types_by_tag: mediaTypesByTag,
  sub_type_tags: subTypeTags = [],
  steps = [STEP_MEDIA_TYPE, STEP_FORMAT_SELECTION],
  download_hostname: downloadHostname,
  collection_id: collectionId,
  site_product_type_id: siteProductTypeId,
  format_data: formatData = {},
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [selectedSubTypeTag, setSelectedSubTypeTag] = useState(null);
  const [selectedFormatIndex, setSelectedFormatIndex] = useState(null);

  const defaultMediaTypes = useMemo(
    () => mediaTypesByTag[DEFAULT_TAG_KEY] || [],
    [mediaTypesByTag],
  );

  // Look up media types for an explicit tag, falling back to the default
  // bucket when no tag is selected. A tag that's missing from the lookup
  // returns [] rather than silently reverting to default — the presenter
  // guarantees every tag in sub_type_tags has a non-empty entry, so a miss
  // surfaces as an empty UI (easier to spot) instead of wrong options.
  const mediaTypesForTag = useCallback((tagId) => {
    if (!tagId) return defaultMediaTypes;
    return mediaTypesByTag[String(tagId)] || [];
  }, [mediaTypesByTag, defaultMediaTypes]);

  const visibleMediaTypes = mediaTypesForTag(selectedSubTypeTag);

  // Filter the configured step list down to steps that actually need user
  // input. Steps whose options collapse to a single choice are skipped and
  // the lone value is pre-selected in handleOpen. This keeps the back button
  // from dropping a user on a step they never meaningfully interacted with.
  //
  // media_type is shown if any variant (default or per-tag) has >1 option —
  // the per-tag filtering happens at render time against visibleMediaTypes.
  const effectiveSteps = useMemo(() => (
    // Drop unknown step names so a typo in SiteProductType::BULK_DOWNLOAD_CONFIG
    // fails loudly (missing step) instead of silently rendering a blank modal
    // body where none of the currentStep === STEP_* render branches match.
    steps.filter(step => VALID_STEPS.includes(step)).filter((step) => {
      if (step === STEP_SUB_TYPE_TAG) return subTypeTags.length > 1;
      if (step === STEP_MEDIA_TYPE) {
        return Object.values(mediaTypesByTag).some(list => list.length > 1);
      }
      return true;
    })
  ), [steps, subTypeTags, mediaTypesByTag]);

  const [currentStep, setCurrentStep] = useState(effectiveSteps[0] || TERMINAL_STEP);

  const nextStepAfter = useCallback((step) => {
    const idx = effectiveSteps.indexOf(step);
    return idx >= 0 ? effectiveSteps[idx + 1] : undefined;
  }, [effectiveSteps]);

  const prevStepBefore = useCallback((step) => {
    const idx = effectiveSteps.indexOf(step);
    return idx > 0 ? effectiveSteps[idx - 1] : undefined;
  }, [effectiveSteps]);

  const handleOpen = useCallback(() => {
    // Pre-seed selections for any collapsed steps that were filtered out of
    // effectiveSteps so downstream rendering still has the value it needs.
    const initialSubTypeTagId = subTypeTags.length === 1 ? subTypeTags[0].id : null;
    const initialMediaTypes = mediaTypesForTag(initialSubTypeTagId);
    const hasSingleMediaType = initialMediaTypes.length === 1;
    setSelectedMediaType(hasSingleMediaType ? initialMediaTypes[0].key : null);
    setSelectedSubTypeTag(initialSubTypeTagId);
    setSelectedFormatIndex(null);
    // effectiveSteps keeps STEP_MEDIA_TYPE if *any* tag's bucket has >1
    // options, but the pre-seed may have already resolved it for this user
    // (single sub-type tag whose bucket is a singleton). Skip past it so the
    // user doesn't land on a one-button step.
    const firstStep = effectiveSteps[0] || TERMINAL_STEP;
    const skipCollapsedMediaType = firstStep === STEP_MEDIA_TYPE && hasSingleMediaType;
    setCurrentStep(skipCollapsedMediaType
      ? (nextStepAfter(STEP_MEDIA_TYPE) || TERMINAL_STEP)
      : firstStep);
    setIsOpen(true);
  }, [subTypeTags, effectiveSteps, mediaTypesForTag, nextStepAfter]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleMediaTypeSelect = useCallback((mediaTypeKey) => {
    setSelectedMediaType(mediaTypeKey);
    setCurrentStep(nextStepAfter(STEP_MEDIA_TYPE) || TERMINAL_STEP);
  }, [nextStepAfter]);

  const handleSubTypeTagSelect = useCallback((tagId) => {
    setSelectedSubTypeTag(tagId);
    // If the chosen tag narrows media types to a single option, pre-select it
    // and jump past STEP_MEDIA_TYPE so the user isn't parked on a one-button
    // page they have to click through. Mirrors the collapsed-step handling in
    // handleOpen.
    const next = nextStepAfter(STEP_SUB_TYPE_TAG);
    if (next === STEP_MEDIA_TYPE) {
      const mts = mediaTypesForTag(tagId);
      if (mts.length === 1) {
        setSelectedMediaType(mts[0].key);
        setCurrentStep(nextStepAfter(STEP_MEDIA_TYPE) || TERMINAL_STEP);
        return;
      }
    }
    setCurrentStep(next || TERMINAL_STEP);
  }, [nextStepAfter, mediaTypesForTag]);

  // Map each step to the setter that clears its selection on back navigation.
  // Keyed by the step being *left* so handleBack uses a single convention.
  const stepClearers = useMemo(() => ({
    [STEP_MEDIA_TYPE]: setSelectedMediaType,
    [STEP_SUB_TYPE_TAG]: setSelectedSubTypeTag,
    [STEP_FORMAT_SELECTION]: setSelectedFormatIndex,
  }), []);

  const handleBack = useCallback(() => {
    let prev = prevStepBefore(currentStep);
    if (!prev) return;

    // Clear the selection made on the step we're leaving so the user has to
    // re-choose before advancing again.
    const clear = stepClearers[currentStep];
    if (clear) clear(null);

    // Mirror the forward auto-skip in handleSubTypeTagSelect: if an upstream
    // step's options collapsed to one under the current selection, the user
    // never meaningfully interacted with it on the way forward, so keep
    // backing up past it instead of parking on a single-button page.
    while (prev === STEP_MEDIA_TYPE && mediaTypesForTag(selectedSubTypeTag).length <= 1) {
      const further = prevStepBefore(prev);
      if (!further) break;
      const clearSkipped = stepClearers[prev];
      if (clearSkipped) clearSkipped(null);
      prev = further;
    }

    setCurrentStep(prev);
  }, [currentStep, prevStepBefore, stepClearers, mediaTypesForTag, selectedSubTypeTag]);

  useEffect(() => {
    if (defaultMediaTypes.length === 0) return;

    const handleBulkDownloadOpen = (e) => {
      const { collectionId: cid, siteProductTypeId: sptid } = e.detail;
      if (cid === collectionId && sptid === siteProductTypeId) {
        handleOpen();
      }
    };

    document.addEventListener('bulk-download:open', handleBulkDownloadOpen);
    return () => document.removeEventListener('bulk-download:open', handleBulkDownloadOpen);
  }, [collectionId, siteProductTypeId, defaultMediaTypes, handleOpen]);

  if (defaultMediaTypes.length === 0) {
    return null;
  }

  // Steps with only one option are filtered out of effectiveSteps, so the
  // presence of a previous visible step is sufficient to decide whether BACK
  // should render — there are no hidden steps to navigate back through.
  const showBackButton = !!prevStepBefore(currentStep);

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} className="collection-download-modal">
      <div className="collection-download-modal__body">
        <div className="collection-download-modal__header">
          <h3>
            <DownloadIcon className="collection-download-modal__download-icon" />
            {sectionName}
          </h3>
          {currentStep === STEP_MEDIA_TYPE && (
            <p className="collection-download-modal__subtitle">SELECT YOUR MEDIA TYPE</p>
          )}
          {currentStep === STEP_SUB_TYPE_TAG && (
            <p className="collection-download-modal__subtitle">SELECT YOUR PRODUCT TYPE</p>
          )}
          {currentStep === STEP_FORMAT_SELECTION && (
            <p className="collection-download-modal__subtitle">SELECT YOUR PREFERENCE</p>
          )}
        </div>

        {currentStep === STEP_MEDIA_TYPE && (
          <div className="collection-download-modal__options">
            {visibleMediaTypes.map(type => (
              <button
                key={type.key}
                type="button"
                className="collection-download-modal__option"
                onClick={() => handleMediaTypeSelect(type.key)}
                data-media-type={type.key}
              >
                {type.label}
              </button>
            ))}
            {showBackButton && (
              <div className="collection-download-modal__actions">
                <button
                  type="button"
                  className="collection-download-modal__back"
                  onClick={handleBack}
                >
                  BACK
                </button>
              </div>
            )}
          </div>
        )}

        {currentStep === STEP_SUB_TYPE_TAG && (
          <div className="collection-download-modal__options">
            {subTypeTags.map(tag => (
              <button
                key={tag.id}
                type="button"
                className="collection-download-modal__option"
                onClick={() => handleSubTypeTagSelect(tag.id)}
                data-sub-type-tag={tag.id}
              >
                {tag.name}
              </button>
            ))}
            {showBackButton && (
              <div className="collection-download-modal__actions">
                <button
                  type="button"
                  className="collection-download-modal__back"
                  onClick={handleBack}
                >
                  BACK
                </button>
              </div>
            )}
          </div>
        )}

        {currentStep === STEP_FORMAT_SELECTION && (
          <FormatSelectionStep
            formatData={formatData}
            selectedMediaType={selectedMediaType}
            selectedSubTypeTag={selectedSubTypeTag}
            downloadHostname={downloadHostname}
            collectionId={collectionId}
            siteProductTypeId={siteProductTypeId}
            selectedFormatIndex={selectedFormatIndex}
            onSelectFormat={setSelectedFormatIndex}
            showBackButton={showBackButton}
            onBack={handleBack}
            onDownloadStarted={handleClose}
          />
        )}
      </div>
    </Modal>
  );
}

function FormatSelectionStep({
  formatData,
  selectedMediaType,
  selectedSubTypeTag,
  downloadHostname,
  collectionId,
  siteProductTypeId,
  selectedFormatIndex,
  onSelectFormat,
  showBackButton,
  onBack,
  onDownloadStarted,
}) {
  const tagKey = selectedSubTypeTag ? String(selectedSubTypeTag) : DEFAULT_TAG_KEY;
  const mediaTypeVariants = formatData[selectedMediaType] || {};
  // A selected tag that's missing from the lookup should not silently fall
  // back to the default bucket — that bucket isn't filtered to the tag, so
  // the user would see formats that may not be valid. Mirror mediaTypesForTag
  // above: surface a miss as an empty list (handled by the "No download
  // formats available." branch below) instead of serving the wrong formats.
  const mediaTypeData = mediaTypeVariants[tagKey] || {};
  const optionsHash = mediaTypeData.options_hash || '';
  const formatOptions = useMemo(() => mediaTypeData.format_options || [], [optionsHash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate from saved preferences when format options change
  useEffect(() => {
    const stored = getStoredPreference(optionsHash);
    if (stored && stored.length === 2) {
      const [savedResolution, savedFiletype] = stored;
      const matchIdx = formatOptions.findIndex(opt =>
        opt.filetype === savedFiletype && opt.resolution === savedResolution
      );
      if (matchIdx >= 0) {
        onSelectFormat(matchIdx);
        return;
      }
    }
    if (formatOptions.length > 0) {
      onSelectFormat(0);
    }
  }, [optionsHash, onSelectFormat]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = useCallback(() => {
    if (selectedFormatIndex === null || !formatOptions[selectedFormatIndex]) return;

    const option = formatOptions[selectedFormatIndex];
    const encodedParams = encodeDownloadParams(option.filetype, option.resolution, selectedSubTypeTag);
    const url = buildDownloadUrl(downloadHostname, collectionId, siteProductTypeId, encodedParams);

    savePreferences(optionsHash, option.resolution, option.filetype);

    // Update window.widget_defaults for in-page consistency
    if (typeof window !== 'undefined') {
      window.widget_defaults = window.widget_defaults || {};
      window.widget_defaults[optionsHash] = [option.resolution || '', option.filetype];
    }

    triggerDownload(url);
    onDownloadStarted();
  }, [
    downloadHostname, selectedFormatIndex, formatOptions, selectedSubTypeTag,
    collectionId, siteProductTypeId, optionsHash, onDownloadStarted,
  ]);

  const handleListKeyDown = useCallback((e) => {
    if (selectedFormatIndex === null) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      onSelectFormat(Math.min(selectedFormatIndex + 1, formatOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onSelectFormat(Math.max(selectedFormatIndex - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDownload();
    }
  }, [selectedFormatIndex, formatOptions.length, onSelectFormat, handleDownload]);

  if (formatOptions.length === 0) {
    return (
      <div className="collection-download-modal__format-selection">
        <p>No download formats available.</p>
      </div>
    );
  }

  return (
    <div className="collection-download-modal__format-selection">
      <ul
        className="collection-download-modal__format-list"
        role="listbox"
        tabIndex={0}
        aria-activedescendant={selectedFormatIndex !== null ? `format-option-${selectedFormatIndex}` : undefined}
        onKeyDown={handleListKeyDown}
      >
        {formatOptions.map((option, idx) => {
          const isSelected = selectedFormatIndex === idx;
          const fileCount = `(${option.count} ${option.count === 1 ? 'file' : 'files'})`;

          return (
            <li
              key={`${option.filetype}-${option.resolution || 'none'}`}
              id={`format-option-${idx}`}
              role="option"
              aria-selected={isSelected}
              className={`collection-download-modal__format-item${isSelected ? ' active' : ''}`}
              onClick={() => onSelectFormat(idx)}
              data-filetype={option.filetype}
              data-resolution={option.resolution}
            >
              {isSelected && (
                <div className="collection-download-modal__check" aria-hidden="true">
                  <CheckMark />
                </div>
              )}
              <div className="collection-download-modal__format-label">
                <span>
                  {`${option.display_filetype} \u2014 ${option.resolution}`}
                </span>
                <span className="collection-download-modal__format-label__count">{fileCount}</span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="collection-download-modal__actions">
        <button
          type="button"
          className="collection-download-modal__download button"
          onClick={handleDownload}
          disabled={selectedFormatIndex === null}
          data-testid="download-button"
        >
          DOWNLOAD
        </button>

        {showBackButton && (
          <button
            type="button"
            className="collection-download-modal__back"
            onClick={onBack}
          >
            BACK
          </button>
        )}
      </div>
    </div>
  );
}

FormatSelectionStep.propTypes = {
  formatData: PropTypes.object.isRequired,
  selectedMediaType: PropTypes.string,
  selectedSubTypeTag: PropTypes.number,
  downloadHostname: PropTypes.string.isRequired,
  collectionId: PropTypes.number.isRequired,
  siteProductTypeId: PropTypes.number.isRequired,
  selectedFormatIndex: PropTypes.number,
  onSelectFormat: PropTypes.func.isRequired,
  showBackButton: PropTypes.bool,
  onBack: PropTypes.func,
  onDownloadStarted: PropTypes.func.isRequired,
};

CollectionDownloadModal.propTypes = {
  section_name: PropTypes.string.isRequired,
  media_types_by_tag: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
  ).isRequired,
  sub_type_tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  format_data: PropTypes.object,
  steps: PropTypes.arrayOf(PropTypes.oneOf(VALID_STEPS)),
  download_hostname: PropTypes.string.isRequired,
  collection_id: PropTypes.number.isRequired,
  site_product_type_id: PropTypes.number.isRequired,
};

export default CollectionDownloadModal;
